import { ChevronRight, Info } from "lucide-react";
import React, { type ButtonHTMLAttributes } from "react";
import { cn } from "@repo/utils";
import { CHEVRON_POSITION } from "./nav-icon-button.config.js";
import { Button } from "../button/button.js";
import type { Sizes } from "../button/button.types.js";
import { useDialog } from "../dialog/use-dialog.js";

type Props = ButtonHTMLAttributes<HTMLButtonElement> & {
  size?: Sizes;
  text: string;
  isOpen: boolean;
  isActive?: boolean;
  icon?: React.ReactNode;
  customButtonClassName?: string;
  chevronPosition?: "90" | "180";
  showChevron?: boolean;
  toggleOpen: (e?: React.MouseEvent) => void;
  showEmptyInfo?: boolean;
  onResetEmptyInfo?: () => void;
};

const textSizes: Record<string, string> = {
  sm: "!text-xs",
  md: "!text-sm",
  lg: "!text-base",
  xl: "!text-lg",
};

export const NavIconButton = ({
  size = "md",
  text,
  isOpen,
  isActive,
  icon,
  customButtonClassName,
  chevronPosition = "90",
  showChevron = false,
  toggleOpen,
  showEmptyInfo = false,
  onResetEmptyInfo,
  ...props
}: Props) => {
  const { closeDialog, showDialog } = useDialog();
  const ChevronComponent = CHEVRON_POSITION[chevronPosition] || ChevronRight;
  const textClass = textSizes[size];

  const iconElement = icon && (
    <Button
      tabIndex={-1}
      icon={icon}
      align="center"
      justify="center"
      data-allow-navigation
      variant={isActive ? "primary" : "outline"}
      size={size}
      as={"div"}
      className="rounded-full"
    />
  );

  return (
    <Button
      variant="ghost"
      fullWidth
      size={size}
      data-allow-navigation={showChevron ? true : undefined}
      icon={iconElement}
      className={cn("gap-1 rounded-lg px-0", customButtonClassName)}
      onClick={(e) => {
        toggleOpen(e);
      }}
      {...props}
    >
      <div className="flex w-full flex-row items-center justify-between">
        <span className={cn(textClass)}>{text}</span>
        {!showChevron && showEmptyInfo && (
          <div
            className="cursor-pointer pr-1"
            title="This item is empty"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              showDialog({
                title: "Information Dialog",
                description: "This item is empty. Clicking on it will not expand or navigate.",
                confirmText: "Ok",
                onConfirm: () => {
                  onResetEmptyInfo?.();
                  closeDialog();
                },
              });
            }}
          >
            <Info className="h-5 w-5 text-gray-600 transition-transform hover:scale-125" />
          </div>
        )}
        {showChevron && (
          <ChevronComponent
            className={cn("h-5 w-5 cursor-pointer text-gray-600 transition-transform", {
              "rotate-90": isOpen && chevronPosition == "90",
              "rotate-180": !isOpen && chevronPosition == "180",
            })}
            onClick={(e: React.MouseEvent) => {
              e.preventDefault();
              e.stopPropagation();
              toggleOpen(e);
            }}
          />
        )}
      </div>
    </Button>
  );
};

export default NavIconButton;
